import { useEffect, useMemo, useRef, useState } from 'react';
import wallpaper from './assets/wallpaper.gif';
import apps from './data/apps';
import DesktopIntro from './components/DesktopIntro';
import DesktopShortcuts from './components/DesktopShortcuts';
import AppWindow from './components/AppWindow';
import StartMenu from './components/StartMenu';
import PreviewModal from './components/PreviewModal';
import Taskbar from './components/Taskbar';
import './App.css';

function App() {
  const [isStartOpen, setIsStartOpen] = useState(false);
  const [openWindows, setOpenWindows] = useState([]);
  const [activeWindowId, setActiveWindowId] = useState(null);
  const [clock, setClock] = useState(() => formatClock(new Date()));
  const [iframePreview, setIframePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const shellRef = useRef(null);
  const startMenuRef = useRef(null);
  const startButtonRef = useRef(null);

  const activeApp = useMemo(
    () => apps.find((app) => app.id === activeWindowId) ?? apps[0],
    [activeWindowId]
  );

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setClock(formatClock(new Date()));
    }, 1000 * 30);

    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!isStartOpen) return;

      const clickedInsideMenu = startMenuRef.current?.contains(event.target);
      const clickedStartButton = startButtonRef.current?.contains(event.target);

      if (!clickedInsideMenu && !clickedStartButton) {
        setIsStartOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsStartOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isStartOpen]);

  const openApp = (appId) => {
    setOpenWindows((cur) => (cur.includes(appId) ? cur : [...cur, appId]));
    setActiveWindowId(appId);
    setIsStartOpen(false);
  };

  const closeApp = (appId) => {
    setOpenWindows((cur) => {
      const next = cur.filter((id) => id !== appId);
      if (activeWindowId === appId) {
        setActiveWindowId(next[next.length - 1] ?? null);
      }
      return next;
    });
  };

  const focusWindow = (appId) => setActiveWindowId(appId);

  const toggleTaskbarWindow = (appId) => {
    setActiveWindowId((cur) => (cur === appId ? null : appId));
  };

  const closePreview = () => {
    setIframePreview(null);
    setVideoPreview(null);
  };

  return (
    <div className="desktop-shell" ref={shellRef}>
      <img src={wallpaper} className="desktop-wallpaper" alt="Animated desktop wallpaper" />
      <div className="desktop-backdrop" />

      <main className="desktop-workspace">
        <DesktopIntro
          onOpenFeatured={() => openApp('project-one')}
          onBrowseApps={() => setIsStartOpen(true)}
        />

        <DesktopShortcuts apps={apps} onOpenApp={openApp} />

        <section className="window-layer" aria-label="Open app windows">
          {openWindows.map((windowId, index) => {
            const app = apps.find((item) => item.id === windowId);
            if (!app) return null;

            return (
              <AppWindow
                key={app.id}
                app={app}
                isActive={activeWindowId === app.id}
                shiftX={index === 0 ? 0 : (index % 2 === 0 ? -1 : 1) * (10 + index * 10)}
                shiftY={index * 10}
                zIndex={activeWindowId === app.id ? openWindows.length + 3 : index + 2}
                onFocus={() => focusWindow(app.id)}
                onClose={() => closeApp(app.id)}
                onPreviewSite={setIframePreview}
                onPreviewVideo={setVideoPreview}
              />
            );
          })}
        </section>
      </main>

      {isStartOpen && (
        <StartMenu
          ref={startMenuRef}
          apps={apps}
          activeApp={activeApp}
          onOpenApp={openApp}
        />
      )}

      <PreviewModal
        iframeSrc={iframePreview}
        videoSrc={videoPreview}
        onClose={closePreview}
      />

      <Taskbar
        ref={startButtonRef}
        isStartOpen={isStartOpen}
        onToggleStart={() => setIsStartOpen((cur) => !cur)}
        openWindows={openWindows}
        activeWindowId={activeWindowId}
        apps={apps}
        onToggleWindow={toggleTaskbarWindow}
        clock={clock}
      />
    </div>
  );
}

function formatClock(date) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export default App;
